import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { tracked } from '@glimmer/tracking';

import type { PopupData } from 'webapp-types';
import { TimeframeType } from 'webapp/enums/enums';

import joinClasses from 'webapp/helpers/join-classes';
import CommonTimeframePopupComponent from 'webapp/components/common/timeframe/popup';
import CommonTimeframePopupItemComponent from 'webapp/components/common/timeframe/popup/item';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormSelectComponent from 'webapp/components/form/select';
import type { Args } from 'webapp/components/popup/inner/context';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class ForecastsTimeframePopupIndexComponent extends CommonTimeframePopupComponent {
	@tracked selectedEndOfMonth: string | null = null;

	private initialYear: number;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.initialYear = this.currentYear;

		// initialize selectedEndOfMonth from endDate if present
		const popupData = this.popupData as PopupData.Forecast.Timeframe;
		if (popupData.endDate) {
			// endDate format is "YYYY_MM", extract the month part
			const parts = popupData.endDate.split('_');
			if (parts.length === 2) {
				this.selectedEndOfMonth = parts[1]!;
			}
		}
	}

	get isNextArrowDisabled() {
		// Allow only one year forward from the initial year
		return this.currentYear >= this.initialYear + 1;
	}

	get monthOptions() {
		const months = [
			'January',
			'February',
			'March',
			'April',
			'May',
			'June',
			'July',
			'August',
			'September',
			'October',
			'November',
			'December',
		];

		const now = new Date();
		const currentMonth = now.getMonth(); // 0-based (0 = January, 11 = December)
		const currentYear = now.getFullYear();

		return months
			.map((month, index) => {
				const monthNumber = (index + 1).toString().padStart(2, '0'); // 01-12
				return {
					value: monthNumber,
					label: `${month} ${this.currentYear}`,
					monthIndex: index,
				};
			})
			.filter((option) => {
				// If we're showing the current year, only show months after the current month
				if (this.currentYear === currentYear) {
					return option.monthIndex > currentMonth;
				}
				// For future years, show all months
				return true;
			});
	}

	@action
	override changeSelected(type: TimeframeType | null, year: number) {
		super.changeSelected(type, year);

		// Clear month selection when year or quarter is selected
		if (
			type === TimeframeType.Y ||
			type === TimeframeType.Q1 ||
			type === TimeframeType.Q2 ||
			type === TimeframeType.Q3 ||
			type === TimeframeType.Q4
		) {
			this.selectedEndOfMonth = null;
		}
	}

	@action
	changeEndOfMonth(month: string) {
		this.selectedEndOfMonth = month;

		// When a month is selected, clear the timeframe since it's one or the other
		if (month) {
			this.selected = null;
		}
	}

	@action
	override goNext() {
		if (this.isNextArrowDisabled) {
			return;
		}
		super.goNext();
	}

	@action
	override apply() {
		const popupData = this.popupData as PopupData.Forecast.Timeframe;

		if (this.selectedEndOfMonth) {
			const endDate = `${this.currentYear}_${this.selectedEndOfMonth}`;
			popupData.onChange(null, endDate);
		} else if (this.selected !== undefined) {
			popupData.onChange(this.selected, null);
		}

		this.close();
	}
	<template>
		<div class="context-ct forecast-timeframe">

			<a
				role="button"
				class={{joinClasses (array "nav-arrow" (if this.isPrevArrowDisabled "disabled"))}}
				{{on "click" this.goPrev}}
			>
				<CommonUiIconComponent @name="ChevronLeft" @size="sm" />
			</a>

			<div class="items-wrapper">
				<div class="row">
					<CommonTimeframePopupItemComponent
						@type={{this.typeYear}}
						@year={{this.currentYear}}
						@dateLabel={{this.yearDateLabel}}
						@selected={{this.selected}}
						@onChange={{this.changeSelected}}
						@isDisabled={{this.isYearDisabled}}
					/>
				</div>
				<div class="row">
					<CommonTimeframePopupItemComponent
						@type={{this.typeQ1}}
						@year={{this.currentYear}}
						@selected={{this.selected}}
						@onChange={{this.changeSelected}}
						@isDisabled={{this.isQ1Disabled}}
					/>
					<CommonTimeframePopupItemComponent
						@type={{this.typeQ2}}
						@year={{this.currentYear}}
						@selected={{this.selected}}
						@onChange={{this.changeSelected}}
						@isDisabled={{this.isQ2Disabled}}
					/>
					<CommonTimeframePopupItemComponent
						@type={{this.typeQ3}}
						@year={{this.currentYear}}
						@selected={{this.selected}}
						@onChange={{this.changeSelected}}
						@isDisabled={{this.isQ3Disabled}}
					/>
					<CommonTimeframePopupItemComponent
						@type={{this.typeQ4}}
						@year={{this.currentYear}}
						@selected={{this.selected}}
						@onChange={{this.changeSelected}}
						@isDisabled={{this.isQ4Disabled}}
					/>
				</div>

				<div class="row end-of-section">
					<label>End of</label>
					<FormSelectComponent
						@selected={{this.selectedEndOfMonth}}
						@options={{this.monthOptions}}
						@onChange={{this.changeEndOfMonth}}
						@isRequired={{false}}
						@isNumeric={{false}}
						@showFloatlabel={{false}}
						@placeholder="Select month"
					/>
				</div>
			</div>

			<a
				role="button"
				class={{joinClasses (array "nav-arrow" (if this.isNextArrowDisabled "disabled"))}}
				{{on "click" this.goNext}}
			>
				<CommonUiIconComponent @name="ChevronRight" @size="sm" />
			</a>

		</div>

		<div class="controls2">

			{{#if this.popupData.onClear}}
				<div class="controls2__left">
					<SubmitButtonComponent
						@text="Clear"
						@theme="grey-empty"
						@size="small"
						@noDelay={{true}}
						@onTrigger={{this.clear}}
					/>
				</div>
			{{/if}}

			<div class="controls2__right">
				<SubmitButtonComponent
					@text="Apply"
					@extraClass="save"
					@onTrigger={{this.apply}}
					@size="small"
				/>
			</div>
		</div>
	</template>
}
